// Funkcje pomocnicze do sprawdzania uprawnień użytkownika

/**
 * Sprawdza czy użytkownik ma dane uprawnienie
 * @param {Object} user - Obiekt użytkownika z permissions
 * @param {string} permissionPath - Ścieżka uprawnienia (np. 'dashboard.view', 'quality.create')
 * @returns {boolean}
 */
export const hasPermission = (user, permissionPath) => {
  // Owner i admin mają wszystkie uprawnienia
  if (user?.role === 'owner' || user?.role === 'admin') {
    return true;
  }

  if (!user?.permissions || typeof user.permissions !== 'object') {
    return false;
  }

  const parts = permissionPath.split('.');
  let current = user.permissions;

  for (const part of parts) {
    if (current && typeof current === 'object' && part in current) {
      current = current[part];
    } else {
      return false;
    }
  }

  return current === true;
};

/**
 * Sprawdza czy użytkownik ma dostęp do menu
 * @param {Object} user - Obiekt użytkownika
 * @param {string} menuItem - Nazwa menu (dashboard, quality, hr, admin, database)
 * @returns {boolean}
 */
export const hasMenuAccess = (user, menuItem) => {
  // Owner i admin mają dostęp do wszystkich menu
  if (user?.role === 'owner' || user?.role === 'admin') {
    return true;
  }

  return hasPermission(user, `menu.${menuItem}`);
};

/**
 * Sprawdza czy użytkownik ma dostęp do funkcjonalności Dashboard
 * @param {Object} user - Obiekt użytkownika
 * @param {string} action - Akcja (view, editCards, addRows, etc.)
 * @returns {boolean}
 */
export const hasDashboardPermission = (user, action = 'view') => {
  if (user?.role === 'owner' || user?.role === 'admin') {
    return true;
  }

  // Dla uprawnień toolbar - sprawdź bezpośrednio bez wymagania dashboard.edit
  if (action.startsWith('toolbar.')) {
    return hasPermission(user, `dashboard.${action}`);
  }

  // Przypomnienia - sprawdź bezpośrednio bez wymagania dashboard.edit
  // (addReminders i deleteReminders to osobne uprawnienia)
  const reminderActions = ['addReminders', 'deleteReminders'];
  if (reminderActions.includes(action)) {
    return hasPermission(user, `dashboard.${action}`);
  }

  // Historia - sprawdź bezpośrednio bez wymagania dashboard.edit
  const historyActions = ['viewHistory', 'deleteHistory'];
  if (historyActions.includes(action)) {
    return hasPermission(user, `dashboard.${action}`);
  }

  // Dla pozostałych akcji edycji, sprawdź najpierw ogólne uprawnienie "edit"
  const editActions = ['editCards', 'addRows', 'deleteRows', 'changeStatus', 'manageSeq', 'editExtendedColumns'];
  if (editActions.includes(action)) {
    // Sprawdź najpierw ogólne uprawnienie "edit"
    if (!hasPermission(user, 'dashboard.edit')) {
      return false;
    }
  }

  return hasPermission(user, `dashboard.${action}`);
};

/**
 * Sprawdza czy użytkownik ma dostęp do funkcjonalności Quality
 * @param {Object} user - Obiekt użytkownika
 * @param {string} action - Akcja (view, create, editAlerts, delete, etc.)
 * @returns {boolean}
 */
export const hasQualityPermission = (user, action = 'view') => {
  if (user?.role === 'owner' || user?.role === 'admin') {
    return true;
  }

  // Dla akcji edycji, sprawdź najpierw ogólne uprawnienie "edit"
  const editActions = ['editAlerts', 'create', 'delete', 'changeStatus', 'changePriority', 'addImages', 'manageArrows'];
  if (editActions.includes(action)) {
    // Sprawdź najpierw ogólne uprawnienie "edit"
    if (!hasPermission(user, 'quality.edit')) {
      return false;
    }
  }

  return hasPermission(user, `quality.${action}`);
};

/**
 * Sprawdza czy użytkownik ma dostęp do funkcjonalności HR
 * @param {Object} user - Obiekt użytkownika
 * @param {string} section - Sekcja (employees, schedule, etc.)
 * @param {string} action - Akcja (view, create, edit, delete)
 * @returns {boolean}
 */
export const hasHRPermission = (user, section, action = 'view') => {
  if (user?.role === 'owner' || user?.role === 'admin') {
    return true;
  }

  // Dla akcji edycji, sprawdź najpierw ogólne uprawnienie "edit"
  const editActions = ['create', 'edit', 'delete'];
  if (editActions.includes(action)) {
    // Sprawdź najpierw ogólne uprawnienie "edit"
    if (!hasPermission(user, 'hr.edit')) {
      return false;
    }
  }

  return hasPermission(user, `hr.${section}.${action}`);
};

/**
 * Sprawdza czy użytkownik ma dostęp do funkcjonalności Admin
 * @param {Object} user - Obiekt użytkownika
 * @param {string} section - Sekcja (users, projects, logos, permissions)
 * @param {string} action - Akcja (view, create, edit, delete)
 * @returns {boolean}
 */
export const hasAdminPermission = (user, section, action = 'view') => {
  // Tylko owner i admin mogą mieć dostęp do Admin
  if (user?.role === 'owner' || user?.role === 'admin') {
    return true;
  }

  // Dla akcji edycji, sprawdź najpierw ogólne uprawnienie "edit"
  const editActions = ['create', 'edit', 'delete', 'changeRole', 'changePassword', 'assignProjects'];
  if (editActions.includes(action)) {
    // Sprawdź najpierw ogólne uprawnienie "edit"
    if (!hasPermission(user, 'admin.edit')) {
      return false;
    }
  }

  return hasPermission(user, `admin.${section}.${action}`);
};

/**
 * Sprawdza czy użytkownik ma dane uprawnienie KPI
 * @param {Object} user - Obiekt użytkownika
 * @param {string} action - Akcja (view, uzupelnijRaportView, uzupelnijRaportDeleteColumnsRows, uzupelnijRaportEditSheetNames, uzupelnijRaportManageSheets, uzupelnijRaportExportExcel)
 * @returns {boolean}
 */
export const hasKpiPermission = (user, action) => {
  if (user?.role === 'owner' || user?.role === 'admin') {
    return true;
  }
  return hasPermission(user, `kpi.${action}`);
};

/**
 * Sprawdza czy użytkownik ma dostęp do Bazy Danych
 * @param {Object} user - Obiekt użytkownika
 * @param {string} action - Akcja (view, editData, create, delete)
 * @returns {boolean}
 */
export const hasDatabasePermission = (user, action = 'view') => {
  // Tylko owner i admin mogą mieć dostęp do Bazy Danych
  if (user?.role === 'owner' || user?.role === 'admin') {
    return true;
  }

  // Dla akcji edycji, sprawdź najpierw ogólne uprawnienie "edit"
  const editActions = ['editData', 'create', 'delete'];
  if (editActions.includes(action)) {
    // Sprawdź najpierw ogólne uprawnienie "edit"
    if (!hasPermission(user, 'database.edit')) {
      return false;
    }
  }

  // Mapuj "edit" na "editData" dla zgodności z backendem
  const mappedAction = action === 'edit' ? 'editData' : action;
  return hasPermission(user, `database.${mappedAction}`);
};
